import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Model } from '../../../api/model';
import { Injector } from '@angular/core';
import { ValidationService } from '../services/validation.service';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { LoaderInstance, LoaderService } from '../../../support/loading/loader.service';
import { ToastService } from '../../../toast/services/toast.service';
import { EntityService } from '../../../entities/services/entity.service';
import { Subject } from 'rxjs/Subject';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { Observable } from 'rxjs/Observable';
import { trans } from '../../../localization/helpers';
import { HookSystemService } from '@kaiser-software/hook-system';

export class ChassisFormHandler {

    /**
     * Form model / data.
     */
    public model: any;

    /**
     * Form structure.
     */
    public structure: any;

    /**
     * Angular FormGroup instance.
     */
    public formGroup: FormGroup;

    /**
     * Form hooks observer.
     */
    public observer: BehaviorSubject<any> = new BehaviorSubject(null);

    /**
     * Contains a subject that is updated when a model is passed
     *
     * @type {ReplaySubject<any>}
     */
    public modelSubject: Subject<Model>;

    /**
     * The FormBuilder instance.
     */
    protected formBuilder: FormBuilder;

    /**
     * The ValidationService instance.
     */
    public validator: ValidationService;

    /**
     * The ToastService instance.
     */
    protected toastService: ToastService;

    /**
     * Backend validation errors.
     */
    public backendErrors: Object = {};

    /**
     * Returns true or false if form is loading.
     *
     * @type {boolean}
     */
    public isLoading = false;

    /**
     * Show built in notifications.
     *
     * @type {boolean}
     */
    public notifications = true;

    /**
     * Form fields holder.
     */
    protected fields = {};

    /**
     * Element type default validations.
     */
    protected ELEMENT_TYPE_DEFAULT_VALIDATIONS = {
        'email': {
            'email': true,
        },
        'url': {
            'url': true,
        }
    };

    /**
     * Processor callback.
     * Override default function to process the form's data with a custom function.
     */
    public processorCallback: Function;

    /**
     * Success callback.
     */
    public successCallbacks = [];

    /**
     * After success callback
     */
    public afterSuccessCallback: Function;

    /**
     * Error callback.
     */
    public errorCallbacks = [];

    public loaderService: LoaderService;

    public loader: LoaderInstance;

    /**
     * Indicator whether an entity is being processed or not.
     *
     * @type {boolean}
     * @default false
     */
    public usesEntity: boolean = false;

    /**
     * The entity name in case an entity is used to the form structure.
     * @type {string}
     */
    public entityName: string;

    public builtCallback;

    /**
     * Constructor
     *
     * @param injector
     * @param structure
     * @param model
     */
    constructor(protected injector: Injector, structure: any, model: Object | Subject<Model> | Model = {}, public formName = null) {
        const hookSystem   = injector.get(HookSystemService);
        this.validator     = injector.get(ValidationService);
        this.toastService  = injector.get(ToastService);
        this.loaderService = injector.get(LoaderService);

        // if structure is a string -> get the entity matching this string
        if (typeof structure === 'string') {
            this.usesEntity = true;
            this.entityName = structure;

            const entityService: EntityService = injector.get(EntityService);

            structure = entityService.getDefaultForm(structure);
        } else if (typeof structure === 'object' && structure.length === 2) {
            this.usesEntity = true;
            this.entityName = structure[0];

            const entityService: EntityService = injector.get(EntityService);

            structure = entityService.getFormById(structure[0], structure[1]);
        } else if (typeof structure === 'function') {
            structure = structure(this.injector, model);
        }

        this.loader = this.loaderService.create('form-handler-' + Math.round((Math.random() * 10000)));

        this.formBuilder = new FormBuilder();

        // Add filter for form structure
        this.structure = hookSystem.filter('form-build-structure-' + this.formName, structure);

        hookSystem.filter('form-build-before-build-' + this.formName, this);

        this.build();

        if (this.builtCallback) {
            this.builtCallback(this);
        }

        // Trigger event
        if (this.formName) {
            hookSystem.event('form-build-completed-' + this.formName, this);
        }

        // we accept either a subject or model instance
        if (model instanceof Subject) {
            this.modelSubject = model;
            model.subscribe(loadedModel => {
                this.model = hookSystem.filter('form-handler-loaded-model-' + this.formName, loadedModel);
            });
        } else {
            this.model = hookSystem.filter('form-handler-loaded-model-' + this.formName, model);
        }
    }

    public built(callback: (form: this) => void) {
        this.builtCallback = callback;
    }

    /**
     * Alias for form group valid.
     *
     * @returns {boolean}
     */
    get valid() {
        return this.formGroup.valid;
    }

    /**
     * Get the forms value.
     *
     * @returns {Object | Model}
     */
    get value() {
        return this.model;
    }

    /**
     * Submit
     */
    public submit() {
        try {
            this.makeFormDirty();

            if (this.valid) {
                // Check if a custom processor is given
                if (this.processorCallback) {
                    this.submitObject();
                } else {
                    // Check if an instance of a model is given
                    if (this.model instanceof Model) {
                        this.submitModel();
                    } else {
                        this.throwError('Please provide a model instance');
                    }
                }
            } else {
                this.throwError(trans('Some form fields seem not be valid'));
            }
        } catch (e) {
            this.throwError(trans('Something went wrong. Contact the application developer for further support.'));
            console.error('Form handler error', e);
        }
    }

    /**
     * Submit method when the model is an instance of Model.
     */
    protected submitModel() {
        if (this.model instanceof Model) {
            this.loader.start();

            // Hooks are executed.
            this.validator.run(() => {
                // Save model
                (<Model>this.model).save()
                    .subscribe((model: any) => {
                        this.throwSuccess(model);
                    }, (errors) => {
                        this.throwError(errors);
                        this.setBackendErrors(errors);
                    }, () => {
                        this.loader.stop();
                    });

            }, (errors) => {
                this.throwError(errors);
                this.loader.stop();
            });
        }
    }

    /**
     * Submit method when the model is not an instance of Model.
     */
    protected submitObject() {
        this.loader.start();

        // Hooks are executed.
        this.validator.run(() => {
            if (this.processorCallback) {
                const self = this;

                // Processor callback resolve function
                const resolve = (model) => {
                    self.throwSuccess(model);
                };

                // Processor callback reject function
                const reject = (error) => {
                    self.throwError(error);
                    self.setBackendErrors(error);
                };

                // Execute processor callback
                this.processorCallback(this.model, resolve, reject);
            } else {
                console.error('Form model is not a Model instance and no custom processor function was given.');
            }
            this.loader.stop();
        }, (errors) => {
            this.throwError(errors);
            this.loader.stop();
        });
    }

    /**
     * Throw form success.
     *
     * @param {Object} model
     */
    protected throwSuccess(model?: Model | Object) {
        this.backendErrors = [];

        if (this.successCallbacks.length) {
            const progress = {
                callbacks: this.successCallbacks.length,
                processed: 0,
            };

            this.successCallbacks.forEach((successCallback) => {
                const result = successCallback(model);

                if (result === undefined || result === true) {
                    return this.markCallbackProcessed(progress, model);
                }

                if (result === false) {
                    return this.throwError();
                }

                if (result instanceof Promise) {
                    return result.then((value) => {
                        if (value || value === undefined) {
                            this.markCallbackProcessed(progress, model);
                        } else {
                            this.throwError();
                        }
                    }, (reject) => {
                        this.throwError(reject);

                        console.warn('Plugin rejected processing data.');
                    });
                }

                if (result instanceof Observable) {
                    return result.subscribe((value: boolean) => {
                        if (value || value === undefined) {
                            this.markCallbackProcessed(progress, model);
                        } else {
                            this.throwError();
                        }
                    });
                }
            });
        } else {
            this.throwAfterSuccess(model);
        }
    }

    protected markCallbackProcessed(progress: { callbacks: number, processed: number }, data: any) {
        progress.processed++;

        if (progress.processed === progress.callbacks) {
            this.throwAfterSuccess(data);
        }
    }

    protected throwAfterSuccess(model?: Model | Object) {
        if (this.notifications) {
            this.toastService.success('saved');
        }

        if (this.afterSuccessCallback) {
            this.afterSuccessCallback(model);
        }
    }

    /**
     * Throw form error.
     *
     * @param {string} error
     */
    protected throwError(error?: string | any) {
        if (this.errorCallbacks) {
            this.errorCallbacks.forEach((errorCallback) => {
                errorCallback(error);
            });
        }

        if (this.notifications) {
            if (error) {
                // Replace the unprocessable entity error with
                // a more meaningful erro message
                if (error.status === 422) {
                    return this.toastService.error('error', trans('Seems like some form data is invalid'));
                }

                if (typeof error === 'string') {
                    this.toastService.error('error', error);
                } else {
                    this.toastService.error('error', error.message);
                }
            } else {
                this.toastService.error('error', trans('Something went wrong while saving'));
            }
        }
    }

    /**
     * Set custom function to process the form's data.
     *
     * @param callback
     */
    public processor(callback: (model: any, resolve: (value?) => void, reject: (error?: any) => void) => void) {
        this.processorCallback = callback;
    }

    /**
     * Add a success callback
     *
     * @param callback
     */
    public success(callback: (data?: any, resolve?, reject?) => Promise<any> | void) {
        let callbackToReturn;

        const args = callback.length;

        if (args >= 2 && args <= 3) {
            // The method expects a data, a resolve and an optional reject
            // argument, and creates a promise instead of expecting the
            // callback to return one

            callbackToReturn = (data) => {
                return new Promise((resolve, reject) => {
                    callback(data, resolve, reject);
                });
            };
        } else {
            // The method expects the callback to return a promise instance
            callbackToReturn = callback;
        }

        this.successCallbacks.push(callbackToReturn);
    }

    /**
     * Set the after success callback
     *
     * @param {(model?: any) => void} callback
     */
    public afterSuccess(callback: (model?: any) => void) {
        this.afterSuccessCallback = callback;
    }

    /**
     * Add an error callback
     *
     * @param callback
     */
    public error(callback: (error?: any) => void) {
        this.errorCallbacks.push(callback);
    }

    /**
     * Submit callback
     *
     * @param callback
     * @deprecated
     */
    public onSubmit(callback: any) {
        console.warn('onSubmit is deprecated. Use success instead');
        this.success(callback);
    }

    /**
     * Update the options of a form element
     *
     * @param {string} name
     * @param callback
     */
    public updateFormElement(name: string, callback) {
        let formElement;

        this.structure.groups.forEach(group => {
            group.sections.forEach(section => {
                section.rows.forEach(row => {
                    row.elements.forEach(element => {
                        if (element.name === name) {
                            formElement = element;
                        }
                    });
                });
            });
        });

        if (formElement) {
            // formElement = Object.assign(formElement, options);
            callback(formElement);
        }

        return this;
    }

    /**
     * Indicates if an element exists with the given name.
     *
     * @param name - The element name.
     */
    public hasElement(name: string): boolean {
        return !!this.getElement(name);
    }

    /**
     * Get the first element with the given name.
     *
     * @param name - The element name.
     */
    public getElement(name: string): any | null {
        const groups = this.structure.groups;

        for (const g of groups) {
            for (const s of g.sections) {
                for (const r of s.rows) {
                    for (const e of r.elements) {
                        if (e.name === name) {
                            return e;
                        }
                    }
                }
            }
        }

        return null;
    }

    /**
     * Remove the element with the given name.
     *
     * @param name - The element name.
     */
    public removeElement(name: string): void {
        const groups = this.structure.groups;

        for (const g of groups) {
            for (const s of g.sections) {
                for (const r of s.rows) {
                    for (const index in r.elements) {
                        const e = r.elements[index];

                        if (e.name === name) {
                            return r.elements.splice(index, 1);
                        }
                    }
                }
            }
        }
    }

    protected setBackendErrors(errors): void {
        if (errors.errors) {
            this.backendErrors = errors.errors;
            return;
        }

        if (errors.body && errors.body.errors) {
            this.backendErrors = errors.body.errors;
        }
    }

    protected makeFormDirty(): void {
        for (const key in this.formGroup.controls) {
            if (this.formGroup.controls.hasOwnProperty(key)) {
                const item = this.formGroup.controls[key];
                item.markAsDirty();
            }
        }
    }

    /**
     * Generate the forms structure
     */
    public build() {
        this.iterateGroups(this.structure['groups']);
        this.formGroup = this.formBuilder.group(this.fields);
    }

    /**
     * Iterate trough groups
     *
     * @param groups
     */
    protected iterateGroups(groups: Array<Object>) {
        if (groups) {
            for (const group of groups) {
                this.iterateSections(group['sections']);
            }
        }
    }

    /**
     * Iterate trough sections
     *
     * @param sections
     */
    protected iterateSections(sections: Array<Object>) {
        if (sections) {
            for (const section of sections) {
                this.iterateRows(section['rows']);
            }
        }
    }

    /**
     * Iterate trough rows
     *
     * @param rows
     */
    protected iterateRows(rows: Array<Object>) {
        if (rows) {
            for (const row of rows) {
                this.iterateElements(row['elements']);
            }
        }
    }

    /**
     * Iterate trough elements
     *
     * @param elements
     */
    protected iterateElements(elements: Array<Object>) {
        if (elements) {
            for (const element of elements) {
                element['validation'] = this.mergeWithDefaultValidations(element);
                this.iterateValidations(element, element['validation']);
            }
        }
    }

    /**
     * Merge validations with default field validations
     *
     * @param element
     * @returns {any}
     */
    protected mergeWithDefaultValidations(element) {
        const defaultValidations = this.ELEMENT_TYPE_DEFAULT_VALIDATIONS[element['type']];
        const validations        = element.validation;

        if (defaultValidations) {
            for (const validation in defaultValidations) {
                if (defaultValidations.hasOwnProperty(validation)) {
                    validations[validation] = defaultValidations[validation];
                }
            }
        }

        return validations;
    }

    /**
     * Iterate trough validations
     * Add all fields with it's validators to the fields object
     *
     * @param element
     * @param validations
     */
    protected iterateValidations(element: Object, validations: Array<Object>) {
        if (validations) {
            const validators = [];

            for (const validation in validations) {
                if (validations.hasOwnProperty(validation)) {
                    if (this.validator.validators.exists(validation)) {
                        validators.push(this.validator.validators.get(validation).getValidatorFn(validations[validation]));
                    }
                }
            }

            if (element['type'] === 'checkbox' && element['config'] && element['config']['options'] && element['config']['options'].length > 0) {
                for (const option of element['config']['options']) {
                    this.fields[element['name'] + '_' + option.value] = new FormControl('', Validators.compose(validators));
                }
            } else {
                this.fields[element['name']] = new FormControl('', Validators.compose(validators));
            }
        } else {
            this.fields[element['name']] = new FormControl('');
        }
    }

}





// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/forms/form/classes/form-handler.ts